import React, { useCallback, useEffect, useState } from 'react';
import GeckoFeatures from 'services/gecko-features';
import { FEATURE_GECKO_INTEGRATIONS } from 'chat-constants/features';
import { connect } from 'react-redux';
import GeckoLocation from 'services/gecko-location';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import Loader from 'components/Loader';
import {
  GECKO_CONSTS,
  GeckoCol,
  GeckoLoadingService,
  GeckoModal,
  GeckoModalService,
  GeckoRow,
  GeckoToastService,
} from 'ui';

import ConnectLoginModal from '../components/ConnectLoginModal.jsx';
import DynamicsLoginModal from '../components/DynmaicsLoginModal';
import RadiusLoginModal from '../components/RadiusLoginModal.jsx';
import SftpSetupModal from '../components/SftpSetupModal';
import WebhookEditModal from '../components/WebhookEditModal';
import TeamsSetupModal from '../components/TeamsSetupModal';

import GeckoIntegrationSetupModal from './GeckoIntegrationSetupModal';

import './index.scss';
import { deleteIntegration } from '../../../actions/integrations';
import WebSafeError from 'services/websafe-error';
import SlackSetupModal from '../components/SlackSetupModal';
import { useGeckoIntegrations } from 'services/gecko-integrations';
import GeckoAuth from 'services/gecko-auth';
import IntegrationCard from './IntegrationCard';
import { useFeature } from 'services/hooks/index.js';

const salesforceImg = `${process.env.PUBLIC_URL}/integrations/salesforce.png`;
const salesforceAltImg = `${process.env.PUBLIC_URL}/integrations/salesforce_alt.png`;
const radiusImg = `${process.env.PUBLIC_URL}/integrations/anthology.png`;
const microsoftDynamicsImg = `${process.env.PUBLIC_URL}/integrations/microsoft-dynamics.png`;
const connectImg = `${process.env.PUBLIC_URL}/integrations/anthology.png`;
const engageImg = `${process.env.PUBLIC_URL}/integrations/engage.png`;
const slateImg = `${process.env.PUBLIC_URL}/integrations/slate.png`;
const ellucianImg = `${process.env.PUBLIC_URL}/integrations/ellucian.png`;
const sftpImg = `${process.env.PUBLIC_URL}/integrations/sftp.png`;
const webhookImg = `${process.env.PUBLIC_URL}/integrations/webhooks.png`;
const slackImg = `${process.env.PUBLIC_URL}/integrations/slack.png`;
const teamsImg = `${process.env.PUBLIC_URL}/integrations/teams.png`;

const salesforceIntegrationsServiceType = 'salesforce_integrations';
const bannerIntegrationsServiceType = 'banner_integrations';

const authenticateWithAuth = (accountUUID) => {
  return new Promise((resolve, reject) => {
    const url = `${process.env.REACT_APP_AUTH_URL}/integration/?accountId=${accountUUID}`;
    const popup = window.open(url, 'popup', 'height=600,width=600');
    // https://stackoverflow.com/a/48240128
    const closedCheckTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(closedCheckTimer);
        reject(new WebSafeError('Authentication not completed'));
      }
    }, 500);
    const messageListener = (event) => {
      if (event.origin !== process.env.REACT_APP_AUTH_URL) {
        return;
      }
      try {
        const payload = JSON.parse(event.data);
        resolve(payload);
      } catch (error) {
        reject(error);
      }
      window.removeEventListener('message', messageListener);
      clearInterval(closedCheckTimer);
      popup.close();
    };
    window.addEventListener('message', messageListener);
  });
};

const createEngageIntegration = async () => {
  const account = await GeckoRequest.get('/account');
  const { geckochat, geckoform } = await authenticateWithAuth(account.uuid);
  const newIntegration = {
    type: 'engage',
    configuration: {
      geckochat: {
        claimCode: geckochat.ClaimCode,
        externalId: geckochat.ExternalId,
      },
      geckoform: {
        claimCode: geckoform.ClaimCode,
        externalId: geckoform.ExternalId,
      },
      authType: 'token',
    },
    account_id: account.id,
  };
  return new GeckoLoadingService().resolve(
    GeckoRequest.post('/integration', newIntegration),
  );
};

const useGetIntegrations = (GeckoIntegrations) => {
  const [geckoIntegrations, setGeckoIntegrations] = useState([]);
  const [isLoadingGeckoIntegrations, setIsLoadingGeckoIntegrations] =
    useState(false);
  const [geckoIntegrationsErrors, setGeckoIntegrationsErrors] = useState([]);

  const reloadGeckoIntegrations = useCallback(() => {
    setIsLoadingGeckoIntegrations(true);
    return GeckoIntegrations.getIntegrations()
      .then((result) => {
        if (result.success === false) {
          setGeckoIntegrationsErrors(result.errors);
          return geckoIntegrations;
        }
        setGeckoIntegrationsErrors([]);
        setGeckoIntegrations(result.data);
        return result.data;
      })
      .catch((error) => {
        console.error(error);
        setGeckoIntegrationsErrors([
          {
            title: 'Unknown Error',
            detail: 'An unknown error occurred',
            code: 500,
          },
        ]);
      })
      .finally(() => {
        setIsLoadingGeckoIntegrations(false);
      });
  }, [
    GeckoIntegrations,
    setGeckoIntegrations,
    setIsLoadingGeckoIntegrations,
    setGeckoIntegrationsErrors,
  ]);

  useEffect(() => {
    reloadGeckoIntegrations();
  }, [reloadGeckoIntegrations]);

  return {
    isLoadingGeckoIntegrations,
    geckoIntegrations,
    geckoIntegrationsErrors,
    reloadGeckoIntegrations,
  };
};

export const Integrations = ({ integrations, apiUrl, deleteIntegration }) => {
  const [connectValues, setConnectValues] = useState({});
  const GeckoIntegrations = useGeckoIntegrations({
    getAuthTokenCallback: GeckoAuth.getToken,
  });

  const {
    isLoadingGeckoIntegrations,
    geckoIntegrations,
    geckoIntegrationsErrors,
    reloadGeckoIntegrations,
  } = useGetIntegrations(GeckoIntegrations);

  useEffect(() => {
    if (geckoIntegrationsErrors.length !== 0) {
      geckoIntegrationsErrors.forEach((error) => {
        new GeckoToastService().error(`${error.title}: ${error.detail}`);
      });
    }
  }, [geckoIntegrationsErrors]);

  const doesIntegrationExist = useCallback(
    (type) => {
      const doesLegacyIntegrationExist = integrations.some(
        (integration) => integration.type === type,
      );
      if (type === salesforceIntegrationsServiceType) {
        return (
          doesLegacyIntegrationExist ||
          geckoIntegrations.some((integration) => {
            return (
              integration.secondaryConnectionConfig.connectionType ===
              'Salesforce'
            );
          })
        );
      }

      if (type === bannerIntegrationsServiceType) {
        return geckoIntegrations.some((integration) => {
          return (
            integration.secondaryConnectionConfig.connectionType === 'Ellucian'
          );
        });
      }
      return doesLegacyIntegrationExist;
    },
    [integrations, geckoIntegrations],
  );

  const isIntegrationSetup = useCallback(
    (type) => {
      const isLegacyIntegrationSetup = integrations.some(
        (integration) => integration.type === type,
      );
      if (type === salesforceIntegrationsServiceType) {
        const isNewIntegrationSetup = geckoIntegrations.some((integration) => {
          return (
            integration.secondaryConnectionConfig.connectionType ===
            'Salesforce' && integration.isSetup === true
          );
        });
        return isNewIntegrationSetup || isLegacyIntegrationSetup;
      }
      if (type === bannerIntegrationsServiceType) {
        const isNewIntegrationSetup = geckoIntegrations.some((integration) => {
          return (
            integration.secondaryConnectionConfig.connectionType === 'Ellucian' &&
            integration.isSetup === true
          );
        });
        return isNewIntegrationSetup || isLegacyIntegrationSetup;
      }
      if (isLegacyIntegrationSetup) {
        return true;
      }
      return false;
    },
    [integrations, geckoIntegrations],
  );

  const openConnectModal = useCallback(() => {
    const handleConnectChange = (newValue) => {
      setConnectValues(newValue);
      openModal();
    };
    const openModal = () =>
      new GeckoModalService().open(() => {
        return (
          <ConnectLoginModal
            values={connectValues}
            onChange={handleConnectChange}
          />
        );
      });
    openModal();
  }, [connectValues, setConnectValues]);

  const openDynamicsModal = useCallback(() => {
    return new GeckoModalService().open(() => {
      return <DynamicsLoginModal apiUrl={apiUrl} />;
    });
  }, [apiUrl]);

  const openSftpModal = useCallback((headerText, text) => {
    new GeckoModalService().open(() => {
      return <SftpSetupModal headerText={headerText} text={text} />;
    });
  }, []);

  const openWebhookModal = useCallback(() => {
    new GeckoModalService().open(() => {
      return <WebhookEditModal />;
    });
  }, []);

  const openTeamsModal = useCallback(() => {
    new GeckoModalService().open(() => {
      return <TeamsSetupModal />;
    });
  }, []);

  const openSlackModal = useCallback(() => {
    new GeckoModalService().open(() => {
      return <SlackSetupModal />;
    });
  }, []);

  const openEllucianModal = useCallback(async () => {
    const result = await GeckoIntegrations.createIntegration('Ellucian');
    if (result.success === false) {
      return result.errors.forEach(
        (error) =>
          new GeckoToastService.error(`${error.title}: ${error.detail}`),
      );
    }
    const { integrationId } = result.data;
    new GeckoModalService().open(() => {
      return (
        <GeckoIntegrationSetupModal
          integrationId={integrationId}
          integrationType="Ellucian"
          GeckoIntegrations={GeckoIntegrations}
        />
      );
    }).then(() => {
      reloadGeckoIntegrations();
    });
  }, []);

  const openDeleteConfirmationModal = useCallback(
    (integrationType) => {
      new GeckoModalService().open(() => {
        return (
          <GeckoModal
            name="Delete Integration"
            children={<p>Are you sure you wish to delete this integration?</p>}
            btns={[
              {
                name: 'Cancel',
                secondary: true,
                icon: 'fa fa-times',
                handler: () => {
                  return new GeckoModalService().close();
                },
              },
              {
                type: GECKO_CONSTS.BTN_TYPE_DANGER,
                name: 'Confirm Delete',
                icon: 'fa fa-trash-alt',
                handler: () => {
                  if (
                    [salesforceIntegrationsServiceType, bannerIntegrationsServiceType].includes(integrationType) &&
                    geckoIntegrations.some(
                      (integration) =>
                        integration.secondaryConnectionConfig.connectionType ===
                        (integrationType === salesforceIntegrationsServiceType ? 'Salesforce' : 'Ellucian'),
                    )
                  ) {
                    const existingIntegration = geckoIntegrations.find(
                      (integration) =>
                        integration.secondaryConnectionConfig.connectionType ===
                        (integrationType === salesforceIntegrationsServiceType ? 'Salesforce' : 'Ellucian'),
                    );

                    return GeckoIntegrations.deleteIntegration(
                      existingIntegration.integrationId,
                    ).then((result) => {
                      if (
                        result.success === false &&
                        result.errors.find((error) => error.code !== 404)
                      ) {
                        new GeckoToastService().error(
                          'Error deleting integration',
                        );
                        return;
                      }
                      new GeckoModalService().close();
                      new GeckoToastService().success(
                        'Integration deleted successfully',
                      );
                      reloadGeckoIntegrations();
                    });
                  }
                  GeckoRequest.delete(`/integration/${integrationType}`)
                    .then(() => {
                      const deletedIntegration = integrations.find(
                        (integration) => integration.type === integrationType,
                      );
                      deleteIntegration(deletedIntegration);
                      new GeckoModalService().close();
                      new GeckoToastService().success(
                        `Integration has successfully been deleted.`,
                      );
                      GeckoLocation.goTo('/settings/integrations');
                    })
                    .catch(renderError);
                },
              },
            ]}
          />
        );
      });
    },
    [integrations, geckoIntegrations, deleteIntegration],
  );

  const salesforceIntegrations = () => {
    const salesforceIntegrationService = {
      name: 'Salesforce',
      type: salesforceIntegrationsServiceType,
      isNew: false,
      description:
        'Create leads in Salesforce automatically when you update a Contact.',
      img: salesforceImg,
      setup: async () => {
        const result = await GeckoIntegrations.createIntegration('Salesforce');
        if (result.success === false) {
          return result.errors.forEach(
            (error) =>
              new GeckoToastService.error(`${error.title}: ${error.detail}`),
          );
        }
        const { integrationId } = result.data;
        reloadGeckoIntegrations();
        return new GeckoModalService()
          .open(() => {
            return (
              <GeckoIntegrationSetupModal
                integrationId={integrationId}
                integrationType="Salesforce"
                GeckoIntegrations={GeckoIntegrations}
              />
            );
          })
          .then(() => {
            reloadGeckoIntegrations();
          });
      },
      onContinueClick: async () => {
        const existingIntegration = geckoIntegrations.find(
          (integration) =>
            integration.secondaryConnectionConfig.connectionType ===
            'Salesforce',
        );
        return new GeckoModalService()
          .open(() => {
            return (
              <GeckoIntegrationSetupModal
                integrationId={existingIntegration.integrationId}
                integrationType="Salesforce"
                GeckoIntegrations={GeckoIntegrations}
              />
            );
          })
          .then(() => {
            reloadGeckoIntegrations();
          });
      },
      manage: () => {
        const existingIntegration = geckoIntegrations.find(
          (integration) =>
            integration.secondaryConnectionConfig.connectionType ===
            'Salesforce',
        );
        return GeckoLocation.goTo(
          `/settings/integrations/${existingIntegration.integrationId}`,
        );
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896421-salesforce#h_adad3b0d28',
        );
      },
    };

    const oldSalesforce = {
      name: 'Salesforce',
      type: 'salesforce',
      description:
        'Create leads in Salesforce automatically when you update a Contact.',
      img: salesforceImg,
      isNew: false,
      setup: async () => {
        const salesforce_client_id = process.env.REACT_APP_SALESFORCE_CLIENT_ID;
        const salesforce_redirect_url = encodeURI(
          `${apiUrl}/forward?url=/settings/integrations/callbacks/salesforce`,
        );
        const salesforce_url = `https://login.salesforce.com/services/oauth2/authorize?response_type=code&client_id=${salesforce_client_id}&redirect_uri=${salesforce_redirect_url}`;
        if (!salesforce_client_id) {
          return console.error('no client id');
        }
        window.location = salesforce_url;
      },
      onContinueClick: async () => {
        const existingIntegration = geckoIntegrations.find(
          (integration) =>
            integration.secondaryConnectionConfig.connectionType ===
            'Salesforce',
        );
        return new GeckoModalService()
          .open(() => {
            return (
              <GeckoIntegrationSetupModal
                integrationId={existingIntegration.integrationId}
                integrationType="Salesforce"
                GeckoIntegrations={GeckoIntegrations}
              />
            );
          })
          .then(() => {
            reloadGeckoIntegrations();
          });
      },
      manage: () => {
        GeckoLocation.goTo('/settings/integration/salesforce');
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896421-salesforce#h_adad3b0d28',
        );
      },
    };

    const items = [];

    if (doesIntegrationExist(oldSalesforce.type)) {
      items.push(oldSalesforce);
      salesforceIntegrationService.img = salesforceAltImg;
      salesforceIntegrationService.isNew = true;
    }

    items.push(salesforceIntegrationService);

    return items;
  };

  const ellucianIntegrations = () => {
    const ellucianIntegrationService = {
      name: 'Ellucian Banner',
      type: bannerIntegrationsServiceType,
      description:
        'Sync and share your data between Gecko And Ellucian Banner',
      img: ellucianImg,
      setup: async () => {
        const text = (
          <span>
            <p>
              You can create contacts and pass conversations into your Ellucian
              Banner CRM.
            </p>
            <p>
              Integrating with Ellucian Banner is possible through exporting
              your contacts and conversations to an SFTP server, where Ellucian
              can access them.
            </p>
          </span>
        );

        if (GeckoFeatures.can(FEATURE_GECKO_INTEGRATIONS)) {
          openEllucianModal();
        } else {
          openSftpModal('Setup Your Ellucian Banner Integration', text);
        }
      },
      manage: () => {
        const existingIntegration = geckoIntegrations.find(
          (integration) =>
            integration.secondaryConnectionConfig.connectionType ===
            'Ellucian',
        );
        return GeckoLocation.goTo(
          `/settings/integrations/${existingIntegration.integrationId}`,
        );
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896459-sftp#h_cb6ba8f719',
        );
      },
      onContinueClick: async () => {
        const existingIntegration = geckoIntegrations.find(
          (integration) =>
            integration.secondaryConnectionConfig.connectionType ===
            'Ellucian',
        );

        return new GeckoModalService()
          .open(() => {
            return (
              <GeckoIntegrationSetupModal
                integrationId={existingIntegration.integrationId}
                integrationType="Ellucian"
                GeckoIntegrations={GeckoIntegrations}
              />
            );
          })
          .then(() => {
            reloadGeckoIntegrations();
          });
      },
    };

    const items = [];
    items.push(ellucianIntegrationService);
    return items;
  };

  const integrationSettings = [
    ...salesforceIntegrations(),
    {
      name: 'Radius',
      type: 'radius',
      description:
        'Create contacts and cases directly in your instance of Radius.',
      img: radiusImg,
      setup: () => {
        new GeckoModalService().open(() => {
          return <RadiusLoginModal />;
        });
      },
      manage: () => {
        GeckoLocation.goTo('/settings/integration/radius');
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896444-radius#h_c6ea5647c5',
        );
      },
    },
    {
      name: 'Connect',
      type: 'connect',
      description:
        'Create contacts and push the conversation into the communications log.',
      img: connectImg,
      setup: () => {
        setConnectValues({});
        openConnectModal();
      },
      manage: () => {
        GeckoLocation.goTo('/settings/integration/connect');
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896436-connect#h_70502667ba',
        );
      },
    },
    {
      name: 'Microsoft Dynamics',
      type: 'dynamics',
      description:
        'Create contacts and track the conversation within the contact activity.',
      img: microsoftDynamicsImg,
      setup: openDynamicsModal,
      manage: () => {
        GeckoLocation.goTo('/settings/integration/dynamics');
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6884549-microsoft-dynamics-365#h_775d78858b',
        );
      },
    },
    {
      name: 'Engage',
      type: 'engage',
      description:
        'Integrate with your Gecko Engage account so you can send SMS broadcasts through Chat.',
      img: engageImg,
      setup: async () => {
        try {
          await createEngageIntegration();
          new GeckoToastService().success('GeckoEngage integration created');
          GeckoLocation.goTo('/settings/integration/engage');
        } catch (error) {
          renderError(error);
        }
      },
      manage: () => {
        return GeckoLocation.goTo('/settings/integration/engage');
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/7021745-chat-x-engage-integration',
        );
      },
    },
    {
      name: 'Slate',
      type: 'slate',
      description: 'Create contacts and pass conversations into your Slate CRM',
      img: slateImg,
      setup: () => {
        const text = (
          <span>
            <p>
              You can create contacts and pass conversations into your Slate
              CRM.
            </p>
            <p>
              Integrating with Slate is possible through exporting your contacts
              and conversations to an SFTP server, where Slate can access them.
            </p>
          </span>
        );

        openSftpModal('Setup Your Slate Integration', text);
      },
      manage: () => {
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896441-slate#h_4ccbb9571b',
        );
      },
    },
    ...ellucianIntegrations(),
    {
      name: 'SFTP',
      type: 'sftp',
      description: 'Export contacts and conversations to an SFTP server.',
      img: sftpImg,
      setup: () => {
        const text = (
          <span>
            <p>Export contacts and conversations to an SFTP server.</p>
            <p>
              We will securely transmit data to an SFTP server of your choice.
              This can be used as an intermediary between Gecko and other
              systems (such as CRMs or big data software).
            </p>
          </span>
        );

        openSftpModal('Setup Your SFTP Integration', text);
      },
      manage: () => {
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896459-sftp#h_cb6ba8f719',
        );
      },
    },
    {
      name: 'Webhooks',
      type: 'webhook',
      description: 'Export contacts and conversations through a webhook.',
      img: webhookImg,
      setup: openWebhookModal,
      manage: openWebhookModal,
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896455-webhooks#h_33dc90cea0',
        );
      },
      ready: true,
    },
    {
      name: 'Microsoft Teams',
      type: 'teams',
      description:
        'Receive notifications within Microsoft Teams whenever a new conversation happens in Gecko Chat.',
      img: teamsImg,
      setup: openTeamsModal,
      manage: () => {
        return GeckoLocation.goTo('/settings/integration/webhooks/teams');
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896455-webhooks#h_33dc90cea0',
        );
      },
    },
    {
      name: 'Slack',
      type: 'slack',
      description:
        'Receive notifications within Slack whenever a new conversation happens in Gecko Chat.',
      img: slackImg,
      setup: openSlackModal,
      manage: () => {
        return GeckoLocation.goTo('/settings/integration/webhooks/slack');
      },
      onInfoClick: () => {
        window.open(
          'https://academy.geckoengage.com/en/articles/6896455-webhooks#h_33dc90cea0',
        );
      },
    },
  ];

  const integrationComponents = integrationSettings.map((integration) => {
    const isSetup = isIntegrationSetup(integration.type);
    const doesExist = doesIntegrationExist(integration.type);

    return (
      <GeckoCol
        test_key={integration.type}
        key={integration.type}
        width="12 md-4 lg-3"
      >
        <IntegrationCard
          integrationType={integration.type}
          isSetup={isSetup}
          doesExist={doesExist}
          name={integration.name}
          image={integration.img}
          description={integration.description}
          onInfoClick={integration.onInfoClick}
          onSetupClick={integration.setup}
          onManageClick={integration.manage}
          onContinueSetupClick={integration.onContinueClick}
          onDeleteClick={() => openDeleteConfirmationModal(integration.type)}
          hasNewTag={integration.isNew}
        />
      </GeckoCol>
    );
  });

  return (
    <Loader isLoading={isLoadingGeckoIntegrations}>
      <div className="content-margin Integrations">
        <GeckoRow>{integrationComponents}</GeckoRow>
      </div>
    </Loader>
  );
};

const mapStateToProps = (state) => {
  return {
    apiUrl: state.appState.apiUrl || process.env.REACT_APP_API_URL,
    integrations: state.integrations,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteIntegration: (integration) => {
      dispatch(deleteIntegration(integration));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Integrations);
